// Where the need-first signup parks a finished-but-unsaved posting so it can
// survive a detour (an email that already has an account, or a save that
// failed). Its own module so the postings composer can read the key without
// importing the signup page, which would drag the whole page bundle
// into the dashboard chunk.
export const PENDING_DRAFT_KEY = "sift_pending_need_draft";

export function stashPendingDraft(draft) {
  try {
    sessionStorage.setItem(PENDING_DRAFT_KEY, JSON.stringify(draft));
    return true;
  } catch {
    return false; // private mode or storage disabled; the draft is simply lost
  }
}

/** Read-and-clear. A parked draft is a one-time hand-off, not a template. */
export function takePendingDraft() {
  try {
    const raw = sessionStorage.getItem(PENDING_DRAFT_KEY);
    sessionStorage.removeItem(PENDING_DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/* --- in-progress signup, held across a reload -------------------------------

   Everything the need-first flow collects lived in React state and nowhere
   else, so any reload dropped the visitor back on an empty composer with all
   of it gone. That is not a hypothetical: the clickstream caught someone who
   wrote their need, waited out the draft, approved it and filled in the
   details, then had the page reload underneath them fifteen seconds later and
   leave immediately. Four fifths of this funnel is inside Meta's in-app
   browser, where a reload we never asked for is a normal event — the Android
   webview renderer gets killed under memory pressure and the browser quietly
   reloads.

   localStorage rather than sessionStorage because the tab itself is what
   often dies: a session store would be gone in exactly the case this exists
   to survive. The tradeoff is a name and email sitting on the device for a
   day, so the window is short and finishing signup clears it immediately. */
export const SIGNUP_PROGRESS_KEY = "sift_get_started_progress";

// Long enough to survive a crash, a phone call, or picking it back up in the
// evening. Not so long that a half-written post from last week ambushes
// someone who came back to start something new.
const PROGRESS_TTL_MS = 24 * 60 * 60 * 1000;

export function saveSignupProgress(state) {
  try {
    localStorage.setItem(SIGNUP_PROGRESS_KEY, JSON.stringify({ ...state, savedAt: Date.now() }));
    return true;
  } catch {
    return false; // private mode or a full quota; the flow just isn't protected
  }
}

/** Returns the saved flow state, or null if there is nothing worth restoring.
 *  Audience-scoped: a business's half-written job post must not reappear on
 *  the talent side, where every label around it would be wrong. */
export function loadSignupProgress(audience) {
  try {
    const raw = localStorage.getItem(SIGNUP_PROGRESS_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw);
    if (!saved || saved.audience !== audience) return null;
    if (!saved.savedAt || Date.now() - saved.savedAt > PROGRESS_TTL_MS) {
      localStorage.removeItem(SIGNUP_PROGRESS_KEY);
      return null;
    }
    // Restoring someone onto the first screen is indistinguishable from a
    // fresh start, and announcing a recovery that returns nothing is worse
    // than saying nothing. Only claim a restore when there is real work in it.
    // (The free-text composer this used to also check is gone; what's worth
    // saving now is a drafted post or anything they typed about themselves.)
    const hasWork =
      (saved.positionTitle || "").trim() ||
      (saved.name || "").trim() ||
      (saved.email || "").trim() ||
      (saved.orgName || "").trim() ||
      (saved.partnerCode || "").trim() ||
      (Array.isArray(saved.drafts) && saved.drafts.some((d) => (d?.title || "").trim()));
    return hasWork ? saved : null;
  } catch {
    return null;
  }
}

export function clearSignupProgress() {
  try {
    localStorage.removeItem(SIGNUP_PROGRESS_KEY);
  } catch {
    /* nothing to clear */
  }
}

/* Has the visitor already been shown a pre-form peek this session?

   Both peeks (TalentPeek for companies, PeerPeek for talent) are meant to be
   seen once: they answer the question someone arrives with, then get out of
   the way. Holding that state in component state did not survive two things
   production immediately found:

     - A remount. 9 of 43 visitors re-entered /get-started within one session
       (one of them five times) -- Facebook's in-app browser reloads freely.
       One visitor worked the company form for 76 seconds, got reloaded, and
       was shown the same peek again before they could get back to it.
     - An audience switch. "Actually, I'm looking for work" unmounts one form
       and mounts the other, so a visitor who had just sat through the company
       peek was shown the talent peek 19 seconds later, and left.

   So the flag is per SESSION and deliberately NOT per audience: one peek per
   visit, whichever side they land on. sessionStorage rather than localStorage
   because a genuinely new visit a day later should get the peek again -- it is
   a first-impression screen, not a dismissed banner. */
export const PEEK_SEEN_KEY = "sift_get_started_peek_seen";

export function hasSeenPeek() {
  try {
    return sessionStorage.getItem(PEEK_SEEN_KEY) === "1";
  } catch {
    // Private mode or a webview with storage disabled. Claiming "already seen"
    // is the safer failure: a visitor who never sees the peek still reaches the
    // form, while one stuck in a peek loop cannot.
    return true;
  }
}

export function markPeekSeen() {
  try {
    sessionStorage.setItem(PEEK_SEEN_KEY, "1");
  } catch {
    /* nothing to do — hasSeenPeek() already fails closed */
  }
}

/* Pre-form peeks: ON, deliberately, as something being tested.

   Showing supply before asking for an account is the shape Upwork uses, and
   that is a reasonable prior to test against -- better than the two data points
   we have. Kept behind one flag so it stays a decision rather than a default.

   The one thing the first days actually produced: 5 of 5 exits were the timer,
   not the button. Nobody pressed either CTA. That is far too small to conclude
   anything from, but it is the signal to watch as volume grows -- if it holds,
   the screen is reading as a loading spinner rather than an invitation, and the
   fix is a shorter timer or none at all, not more content on it.

   Honest limit on "testing" this: at current volume a week produces single-
   digit accounts, so this cannot be settled by conversion rate any time soon.
   What it CAN answer sooner is the auto-vs-button split and whether people who
   see it go on to touch the composer at all. */
/* Off, because the evidence it carried now lives on the form itself.

   The peek was the right idea in the wrong place: a screen of real
   availability posts shown BEFORE the signup form, which people skipped to get
   to the thing they came for. AvailabilityTicker carries the same postings
   along the bottom of the form itself, where the decision is actually being
   made, so running both would show the same people twice and put a screen in
   front of a screen.

   Kept as a flag rather than deleted: this is also the switch that keeps the
   one-field form measurable. Two changes moving at once on ~120 visitors per
   five days is how the last decision here got made on one account against
   two. */
export const PEEK_ENABLED = false;


/* The drafted company post, kept across a dismissal.

   `composed` lives in CompanyDoors as React state, so closing the hero panel
   unmounted it and the post went with it. On 2026-09-15 at 11:11 a company
   drafted a role, saw its matches, closed the panel seven seconds later, came
   back in through the sticky bar thirty seconds after that -- and landed on an
   empty form with none of their work. They left. Twice in the same visit they
   had typed a need and watched it evaporate.

   Same storage and TTL as the signup progress beside it, and cleared the
   moment the posting is actually created, so nothing stale is ever restored
   onto a company that has already published. */
const COMPANY_DRAFT_KEY = "sift_company_draft";

export function saveCompanyDraft(draft) {
  try {
    if (!draft || !(draft.title || "").trim()) return false;
    localStorage.setItem(
      COMPANY_DRAFT_KEY,
      JSON.stringify({ ...draft, savedAt: Date.now() })
    );
    return true;
  } catch {
    return false;
  }
}

export function loadCompanyDraft() {
  try {
    const raw = localStorage.getItem(COMPANY_DRAFT_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw);
    if (!saved || !(saved.title || "").trim()) return null;
    if (!saved.savedAt || Date.now() - saved.savedAt > PROGRESS_TTL_MS) {
      localStorage.removeItem(COMPANY_DRAFT_KEY);
      return null;
    }
    return saved;
  } catch {
    return null;
  }
}

export function clearCompanyDraft() {
  try {
    localStorage.removeItem(COMPANY_DRAFT_KEY);
  } catch {
    /* nothing to clear */
  }
}
